import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import { Refresh, Add, Check, Close as CloseIcon, PendingActions, BeachAccess, EventAvailable, Block } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import {
  hrService,
  EmployeeListItem,
  HrSummary,
  LeaveBalance,
  LeaveRequest,
  LeaveRequestStatus,
  LeaveType,
} from '../../services/hrService';

const statusTone: Record<LeaveRequestStatus, StatusTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

const statusLabel: Record<LeaveRequestStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const fmtDays = (v: number | string): string => {
  const n = toNum(v);
  return n === null ? '—' : `${n}`;
};

interface ConfirmState {
  request: LeaveRequest;
  action: 'APPROVED' | 'REJECTED';
}

const LeavePage: React.FC = () => {
  const [summary, setSummary] = useState<HrSummary | null>(null);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusTab, setStatusTab] = useState<LeaveRequestStatus | 'ALL'>('PENDING');

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createEmployee, setCreateEmployee] = useState<EmployeeListItem | null>(null);
  const [createLeaveTypeId, setCreateLeaveTypeId] = useState<number | ''>('');
  const [createFrom, setCreateFrom] = useState('');
  const [createTo, setCreateTo] = useState('');
  const [createReason, setCreateReason] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [balancesEmployee, setBalancesEmployee] = useState<EmployeeListItem | null>(null);
  const [balancesOpen, setBalancesOpen] = useState(false);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesError, setBalancesError] = useState<string | null>(null);

  const loadRequests = () => {
    setLoading(true);
    setError(null);
    hrService
      .getLeaveRequests()
      .then((res) => setRequests(res.leaveRequests))
      .catch((err: any) => setError(err.message || 'Failed to load leave requests'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRequests();
    hrService.getSummary().then(setSummary).catch(() => undefined);
    hrService
      .getEmployees({ limit: 100, status: 'ACTIVE' })
      .then((res) => setEmployees(res.employees))
      .catch(() => undefined);
    hrService.getLeaveTypes().then((res) => setLeaveTypes(res.leaveTypes)).catch(() => undefined);
  }, []);

  const counts = useMemo(() => {
    const c: Record<LeaveRequestStatus, number> = { PENDING: 0, APPROVED: 0, REJECTED: 0, CANCELLED: 0 };
    requests.forEach((r) => {
      c[r.status] += 1;
    });
    return c;
  }, [requests]);

  const filteredRequests = statusTab === 'ALL' ? requests : requests.filter((r) => r.status === statusTab);

  const refreshAll = () => {
    loadRequests();
    hrService.getSummary().then(setSummary).catch(() => undefined);
  };

  const handleConfirmAction = async () => {
    if (!confirm) return;
    setActionBusy(true);
    try {
      await hrService.updateLeaveRequestStatus(confirm.request.id, confirm.action);
      setConfirm(null);
      refreshAll();
    } catch (err: any) {
      setError(err.message || 'Failed to update leave request');
      setConfirm(null);
    } finally {
      setActionBusy(false);
    }
  };

  const openCreate = () => {
    setCreateEmployee(null);
    setCreateLeaveTypeId('');
    setCreateFrom('');
    setCreateTo('');
    setCreateReason('');
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createEmployee || !createLeaveTypeId || !createFrom || !createTo) {
      setCreateError('Employee, leave type, from and to dates are required');
      return;
    }
    setCreateBusy(true);
    setCreateError(null);
    try {
      await hrService.createLeaveRequest({
        employeeId: createEmployee.id,
        leaveTypeId: createLeaveTypeId,
        fromDate: createFrom,
        toDate: createTo,
        reason: createReason || undefined,
      });
      setCreateOpen(false);
      refreshAll();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to submit leave request');
    } finally {
      setCreateBusy(false);
    }
  };

  const openBalances = (employee: EmployeeListItem) => {
    setBalancesEmployee(employee);
    setBalancesOpen(true);
    setBalancesLoading(true);
    setBalancesError(null);
    hrService
      .getLeaveBalances(employee.id)
      .then((res) => setBalances(res.balances))
      .catch((err: any) => setBalancesError(err.message || 'Failed to load balances'))
      .finally(() => setBalancesLoading(false));
  };

  return (
    <Box>
      <PageHeader
        title="Leave"
        subtitle="Requests, approvals and per-employee leave balances"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={refreshAll} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={openCreate}>
              New Leave Request
            </Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Pending Requests" value={summary?.pendingLeaveRequests ?? null} icon={<PendingActions />} color="#d97706" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="On Leave Today" value={summary?.onLeaveToday ?? null} icon={<BeachAccess />} color="#0891b2" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Approved (loaded)" value={counts.APPROVED} icon={<EventAvailable />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Rejected (loaded)" value={counts.REJECTED} icon={<Block />} color="#dc2626" />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Leave Balances
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            options={employees}
            value={balancesEmployee}
            onChange={(_, v) => setBalancesEmployee(v)}
            getOptionLabel={(e) => `${e.first_name} ${e.last_name || ''} (${e.employee_code})`.trim()}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            sx={{ minWidth: 280 }}
            renderInput={(params) => <TextField {...params} label="Employee" placeholder="Search employee…" />}
          />
          <Button variant="outlined" disabled={!balancesEmployee} onClick={() => balancesEmployee && openBalances(balancesEmployee)}>
            View Balances
          </Button>
        </Box>
      </Paper>

      <FilterBar>
        {(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED', 'ALL'] as const).map((s) => (
          <Chip
            key={s}
            label={s === 'ALL' ? `All (${requests.length})` : `${statusLabel[s]} (${counts[s]})`}
            color={statusTab === s ? 'primary' : 'default'}
            variant={statusTab === s ? 'filled' : 'outlined'}
            onClick={() => setStatusTab(s)}
            size="small"
          />
        ))}
        <FilterSpacer />
        <Typography variant="body2" color="text.secondary">
          <strong>{filteredRequests.length}</strong> requests
        </Typography>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Employee', 'Leave Type', 'From', 'To', 'Days', 'Reason', 'Status', 'Applied On', ''].map((h, i) => (
                  <TableCell key={h || i} align={h === 'Days' ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ border: 0 }}>
                    <TableEmptyState title="No leave requests here" hint="Try a different status filter." />
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((r) => (
                  <TableRow key={r.id} sx={bodyRowSx}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.employee_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {r.employee_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.leave_type_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(r.from_date).toLocaleDateString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(r.to_date).toLocaleDateString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtDays(r.days)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={r.reason || undefined}>
                        {r.reason || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={statusLabel[r.status]} tone={statusTone[r.status]} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(r.created_at).toLocaleDateString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {r.status === 'PENDING' && (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Approve">
                            <IconButton size="small" color="success" onClick={() => setConfirm({ request: r, action: 'APPROVED' })}>
                              <Check fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" color="error" onClick={() => setConfirm({ request: r, action: 'REJECTED' })}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* Approve / reject confirmation */}
      <Dialog open={confirm !== null} onClose={() => (actionBusy ? undefined : setConfirm(null))}>
        <DialogTitle>{confirm?.action === 'APPROVED' ? 'Approve Leave Request' : 'Reject Leave Request'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.action === 'APPROVED' ? 'Approve' : 'Reject'} the {confirm ? fmtDays(confirm.request.days) : ''}-day{' '}
            {confirm?.request.leave_type_name} request from <strong>{confirm?.request.employee_name}</strong> (
            {confirm ? new Date(confirm.request.from_date).toLocaleDateString('en-IN') : ''} –{' '}
            {confirm ? new Date(confirm.request.to_date).toLocaleDateString('en-IN') : ''})?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirm?.action === 'APPROVED' ? 'success' : 'error'}
            variant="contained"
            disabled={actionBusy}
          >
            {actionBusy ? 'Working…' : confirm?.action === 'APPROVED' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New leave request */}
      <Dialog open={createOpen} onClose={() => (createBusy ? undefined : setCreateOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New Leave Request</DialogTitle>
        <DialogContent dividers>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete
              size="small"
              options={employees}
              value={createEmployee}
              onChange={(_, v) => setCreateEmployee(v)}
              getOptionLabel={(e) => `${e.first_name} ${e.last_name || ''} (${e.employee_code})`.trim()}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Employee" required />}
            />
            <TextField
              select
              size="small"
              label="Leave Type"
              required
              value={createLeaveTypeId}
              onChange={(e) => setCreateLeaveTypeId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {leaveTypes.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>
                  {lt.name} ({lt.code})
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="From"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                value={createFrom}
                onChange={(e) => setCreateFrom(e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="To"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                value={createTo}
                onChange={(e) => setCreateTo(e.target.value)}
              />
            </Box>
            <TextField
              size="small"
              label="Reason"
              multiline
              minRows={2}
              value={createReason}
              onChange={(e) => setCreateReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={createBusy}>
            {createBusy ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave balances */}
      <Dialog open={balancesOpen} onClose={() => setBalancesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Leave Balances
          {balancesEmployee && (
            <Typography variant="body2" color="text.secondary">
              {balancesEmployee.first_name} {balancesEmployee.last_name} ({balancesEmployee.employee_code})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {balancesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : balancesError ? (
            <Alert severity="error">{balancesError}</Alert>
          ) : balances.length === 0 ? (
            <TableEmptyState title="No leave balances recorded" hint="Balances are created when a leave request is submitted." />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Leave Type</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Entitled
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Taken
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Pending
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>{b.leave_type_name}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDays(b.entitled)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDays(b.taken)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDays(b.pending)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBalancesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeavePage;
